import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton } from '@ui/button';
import { Radio } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { StyledFormWithImage } from '../StyledForm';
import { Question } from '../Question';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import {
  FieldWrapper,
  ImageContainer,
  ButtonContainer,
  MobileImageContainer,
  DesktopImageContainer,
} from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const PupGenderForm = ({
  defaultValues,
  genderList,
  goBack,
  labelName,
  index,
  onSubmit,
  pupGenderQuestionSubheadline,
  pupGenderQuestionPrompt,
  mobileImage,
  desktopImage,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('pupgender_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_gender',
        cart?.customer || customer,
        data,
      ),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledFormWithImage>
      <ImageContainer>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isInSubmittingState}
            type="button"
            variant="dark"
          />
        </ButtonContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper $minHeight={'280'}>
        <FormFieldsContainer $maxWidth={391}>
          <Question
            subheadline={pupGenderQuestionSubheadline}
            prompt={pupGenderQuestionPrompt}
            typography={'heading4'}
            gap={0}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="gender"
                render={({
                  field: { ref, onChange, ...field },
                  fieldState: { error },
                }) => (
                  <>
                    <Radio
                      {...field}
                      variant="sea"
                      showRadioIcon={false}
                      choices={genderList}
                      legendText={labelName}
                      name="gender"
                      error={error?.message || ''}
                      status={error?.message ? 'error' : null}
                      onChange={value => {
                        onChange(value);
                        handleSubmit(submitForm)();
                      }}
                      onClick={event => {
                        const clickedValue = event?.target?.value;
                        /*this handles if a person goes back and selects the same option*/
                        if (field.value === clickedValue) {
                          handleSubmit(submitForm)();
                        }
                      }}
                    />
                  </>
                )}
              />
            </FieldWrapper>
          </Question>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

PupGenderForm.displayName = 'PupGenderForm';

PupGenderForm.propTypes = {
  defaultValues: PropTypes.shape({
    gender: PropTypes.string,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pupGenderQuestionSubheadline: PropTypes.string.isRequired,
  pupGenderQuestionPrompt: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.string,
};
